import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

export interface NotificationPreferences {
  emailEnabled: boolean;
  emailDigestFrequency: 'immediate' | 'daily' | 'disabled';
  emailOnEventCreated: boolean;
  emailOnEventUpdated: boolean;
  emailOnEventDeleted: boolean;
  emailOnMemberJoined: boolean;
  emailOnMemberRemoved: boolean;
  emailOnRoleChanged: boolean;
  emailOnTaskAssigned: boolean;
  emailOnTaskReminder: boolean;

  pushEnabled: boolean;
  pushOnEventCreated: boolean;
  pushOnEventUpdated: boolean;
  pushOnEventDeleted: boolean;
  pushOnMemberJoined: boolean;
  pushOnMemberRemoved: boolean;
  pushOnRoleChanged: boolean;
  pushOnTaskAssigned: boolean;
  pushOnTaskReminder: boolean;
}

function getDefaultPreferences(): NotificationPreferences {
  return {
    emailEnabled: true,
    emailDigestFrequency: 'immediate',
    emailOnEventCreated: true,
    emailOnEventUpdated: true,
    emailOnEventDeleted: true,
    emailOnMemberJoined: true,
    emailOnMemberRemoved: true,
    emailOnRoleChanged: true,
    emailOnTaskAssigned: true,
    emailOnTaskReminder: true,

    pushEnabled: true,
    pushOnEventCreated: true,
    pushOnEventUpdated: true,
    pushOnEventDeleted: true,
    pushOnMemberJoined: true,
    pushOnMemberRemoved: true,
    pushOnRoleChanged: true,
    pushOnTaskAssigned: true,
    pushOnTaskReminder: true,
  };
}

function mapDbToPreferences(dbData: any): NotificationPreferences {
  return {
    emailEnabled: dbData.email_enabled ?? true,
    emailDigestFrequency: dbData.email_digest_frequency ?? 'immediate',
    emailOnEventCreated: dbData.email_on_event_created ?? true,
    emailOnEventUpdated: dbData.email_on_event_updated ?? true,
    emailOnEventDeleted: dbData.email_on_event_deleted ?? true,
    emailOnMemberJoined: dbData.email_on_member_joined ?? true,
    emailOnMemberRemoved: dbData.email_on_member_removed ?? true,
    emailOnRoleChanged: dbData.email_on_role_changed ?? true,
    emailOnTaskAssigned: dbData.email_on_task_assigned ?? true,
    emailOnTaskReminder: dbData.email_on_task_reminder ?? true,

    pushEnabled: dbData.push_enabled ?? true,
    pushOnEventCreated: dbData.push_on_event_created ?? true,
    pushOnEventUpdated: dbData.push_on_event_updated ?? true,
    pushOnEventDeleted: dbData.push_on_event_deleted ?? true,
    pushOnMemberJoined: dbData.push_on_member_joined ?? true,
    pushOnMemberRemoved: dbData.push_on_member_removed ?? true,
    pushOnRoleChanged: dbData.push_on_role_changed ?? true,
    pushOnTaskAssigned: dbData.push_on_task_assigned ?? true,
    pushOnTaskReminder: dbData.push_on_task_reminder ?? true,
  };
}

function mapPreferencesToDb(prefs: NotificationPreferences) {
  return {
    email_enabled: prefs.emailEnabled,
    email_digest_frequency: prefs.emailDigestFrequency,
    email_on_event_created: prefs.emailOnEventCreated,
    email_on_event_updated: prefs.emailOnEventUpdated,
    email_on_event_deleted: prefs.emailOnEventDeleted,
    email_on_member_joined: prefs.emailOnMemberJoined,
    email_on_member_removed: prefs.emailOnMemberRemoved,
    email_on_role_changed: prefs.emailOnRoleChanged,
    email_on_task_assigned: prefs.emailOnTaskAssigned,
    email_on_task_reminder: prefs.emailOnTaskReminder,

    push_enabled: prefs.pushEnabled,
    push_on_event_created: prefs.pushOnEventCreated,
    push_on_event_updated: prefs.pushOnEventUpdated,
    push_on_event_deleted: prefs.pushOnEventDeleted,
    push_on_member_joined: prefs.pushOnMemberJoined,
    push_on_member_removed: prefs.pushOnMemberRemoved,
    push_on_role_changed: prefs.pushOnRoleChanged,
    push_on_task_assigned: prefs.pushOnTaskAssigned,
    push_on_task_reminder: prefs.pushOnTaskReminder,
  };
}

export const useNotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setPreferences(getDefaultPreferences());
      setLoading(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        const { data, error } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 = no rows returned, which is fine (user hasn't set preferences yet)
          throw error;
        }

        if (data) {
          setPreferences(mapDbToPreferences(data));
        } else {
          // First time user, create default preferences
          const defaults = getDefaultPreferences();
          await supabase
            .from('notification_preferences')
            .insert([{ user_id: user.id, ...mapPreferencesToDb(defaults) }]);
          setPreferences(defaults);
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
        setPreferences(getDefaultPreferences());
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  const updatePreference = async <K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) => {
    if (!user || !preferences) return;

    const updated = { ...preferences, [key]: value };
    setPreferences(updated);

    try {
      await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...mapPreferencesToDb(updated),
        })
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      // Revert on error
      setPreferences(preferences);
    }
  };

  return { preferences, loading, updatePreference };
};
